import React, { useState, useEffect } from 'react';
import { useNavigate } from "react-router-dom";
import { Grid, Box, Tab, Tabs, Modal, Button, Typography } from '@mui/material'
import "./MachineSettings.css";
import TextSetting from '../machineSettingsComponents/TextSetting';
import ToggleSetting from '../machineSettingsComponents/ToggleSetting';
import DropDownSetting from '../machineSettingsComponents/DropDownSetting';
import { splitAndFormatString, versionCompare } from '../../../../ts/Common';
import _ from 'lodash'
import { useAlert } from '../../../../AlertContext';
import { useTranslation } from 'react-i18next';
import { migrations } from '../../../../ts/TransformationMigrations';
import { useAppInsightsContext } from '@microsoft/applicationinsights-react-js';

const MY_HOST = import.meta.env.VITE_MY_HOST || "https://spectre-licensing.com"

export default function MachineSettings(props: any) {
    const { isOpen, setIsOpen, settings, setSettings, setSettingsLoaded, machine } = props
    const [settingsArray, setSettingsArray] = useState(new Array<any>())
    const [currentTabIndex, setCurrentTabIndex] = useState(0);
    const { openAlert } = useAlert()
    const navigate = useNavigate();
    const { t } = useTranslation();

    interface TabPanelProps {
        children?: React.ReactNode;
        index: number;
        value: number;
    }

    function CustomTabPanel(props: TabPanelProps) {
        const { children, value, index, ...other } = props;

        return (
            <div
                role="tabpanel"
                hidden={value !== index}
                id={`simple-tabpanel-${index}`}
                aria-labelledby={`simple-tab-${index}`}
                {...other}
            >
                {value === index && (
                    <Box>
                        {children}
                    </Box>
                )}
            </div>
        );
    }

    function navigateToMatchmaking() {
        alert("Failed to connect to machine, redirecting to matchmaking");
        navigate("/matchmaking");
    }

    const handleTabChange = (e: any, tabIndex: any) => {
        setCurrentTabIndex(tabIndex);
    };

    const toggleMachineSettings = function () {
        return setIsOpen(!isOpen)
    }

    const add_value_recursion = (src: any, dst: any) => {
        for (var name in src) {
            var node = src[name]
            if (typeof node === 'object') {
                add_value_recursion(node, dst[name])
            } else if (dst) {
                let this_node = dst[name]
                this_node.value = node

                this_node.Set = async (elementValue: any) => {
                    const settingPath = this_node['path'].join('/')

                    const postBody: any = {
                        "Requests": {
                            "SetSetting": {
                                "Path": `/Settings/${settingPath}`,
                                "Value": elementValue
                            }
                        }
                    }

                    try {
                        const response = await machine.connection.post(postBody);

                        // Check if the response contains an error code (non-zero means error)
                        if (response?.Responses?.SetSetting?.code && response.Responses.SetSetting.code !== "0") {
                            const errorCode = response.Responses.SetSetting.code;
                            const errorDescription = response.Responses.SetSetting.description || "An unknown error occurred";

                            openAlert(
                                errorCode,
                                errorDescription,
                                t('alertContext.ok'),
                                undefined,
                                false,
                                undefined,
                                {
                                    component: "MachineSettings",
                                    action: "setSetting",
                                    settingPath: settingPath,
                                    errorCode: errorCode,
                                    machineVersion: machine?.version ?? "Unknown version",
                                    connectionEndpoint: machine?.connection?.ip ?? "Unknown IP",
                                },
                                'error'
                            );
                            return; // Don't update the value if there was an error
                        }

                        this_node.value = elementValue;
                    } catch (err: any) {
                        console.log("Error setting machine value", err);

                        openAlert(
                            t('alertContext.settingUpdateFailure.title'),
                            t('alertContext.settingUpdateFailure.message'),
                            t('alertContext.ok'),
                            undefined,
                            false,
                            err instanceof Error ? err : new Error(String(err)),
                            {
                                component: "MachineSettings",
                                action: "setSetting",
                                settingPath: settingPath,
                                machineVersion: machine?.version ?? "Unknown version",
                                connectionEndpoint: machine?.connection?.ip ?? "Unknown IP",
                            },
                            'error'
                        );
                    }
                }
            }
        }
    }

    const path_resolve = (obj: any, searchKey: string = "value", path: Array<any> = [], results: Array<any> = []) => {
        const r = results;
        Object.keys(obj).forEach(key => {
            const next_obj = obj[key];
            if (key === searchKey) {
                obj.path = [...path]
                // Preserve existing readonly=true (e.g., from lock state), else infer from Permission
                obj.readonly = obj.readonly === true ? true : (obj.Permission === 'r')
                r.push(obj);
            } else if (typeof next_obj === 'object') {
                path.push(key)
                path_resolve(next_obj, searchKey, path, r);
                path.pop();
            }
        });
        return r;
    };

    const appInsights = useAppInsightsContext();

    useEffect(() => {
        setSettingsLoaded(false);

        // Create a reusable error handler
        const handleSettingsError = (err: Error | undefined, message: string = "Failed to Load Settings from Machine", action: string) => {
            openAlert(
                t('alertContext.settingsLoadFailure.title'),
                t('alertContext.settingsLoadFailure.message'),
                t('alertContext.back'),
                () => {
                    navigate('/matchmaking');
                },
                true,
                err,
                {
                    component: "MachineSettings",
                    action: "",
                    machineVersion: machine?.version ?? "Unknown version",
                    connectionEndpoint: machine?.connection?.ip ?? "Unknown IP",
                }
            );
        };

        // Use async/await for better readability
        const loadSettings = async () => {
            try {
                // Check connection
                const pingResponse = await machine.connection.ping();
                if (!pingResponse) {
                    openAlert(
                        t('alertContext.pingFailure.title'),
                        t('alertContext.pingFailure.message'),
                        t('alertContext.back'),
                        () => navigate('/matchmaking'),
                        false,
                        undefined,
                        {
                            component: "MachineSettings",
                            action: "ping",
                            machineVersion: machine?.version ?? "Unknown version",
                            connectionEndpoint: machine?.connection?.ip ?? "Unknown IP",
                        }
                    );
                    return;
                }

                // Load export formats
                try {
                    const formatData = await machine.connection.get('GetExportFormats');
                    if (formatData && _.has(formatData['Responses']['GetExportFormats'], 'Formats')) {
                        let formats = formatData['Responses']['GetExportFormats']['Formats'];
                        formats.unshift("");
                        machine.settings['Export']['Format']['Option'] = formats;
                    }
                } catch (err) {
                    // Continue even if export formats fail
                    console.log("Warning: Failed to load export formats", err);
                }

                // Load settings
                const data = await machine.connection.get("GetSettings%20GetSettingsInfo");

                if (!data?.Responses?.GetSettings?.Settings || !data?.Responses?.GetSettingsInfo?.Settings) {
                    throw new Error("Unable to retrieve settings from machine");
                }

                let settings_values: any = data["Responses"]["GetSettings"]["Settings"];
                let settings_user = machine.settings;

                settings_user.sync_from_json(settings_values["User"]);
                delete settings_values["User"];

                let settings_info: any = data["Responses"]["GetSettingsInfo"]["Settings"];
                let settings_total: any = settings_info;
                add_value_recursion(settings_values, settings_total);

                for (let i = 0; i < settings_user.All.length; i++) {
                    let path_array = settings_user.All[i]['Path'].split("/");
                    let node_one = path_array[0];
                    let node_two = path_array[1];

                    if (settings_total[node_one] === undefined) {
                        settings_total[node_one] = {};
                    }
                    settings_total[node_one][node_two] = settings_user.All[i];
                }

            // Query machine lock state and, if locked, force all settings to readonly
            // Only attempt this if the machine supports SettingsLocking feature
                try {
                    const lockResponse = await machine.connection.get("islocked");
                    const isLocked = Boolean(lockResponse?.Responses?.IsLocked?.value);
                    if (isLocked) {
                        // Traverse all value nodes and set readonly = true
                        const allNodes = path_resolve(settings_total);
                        allNodes.forEach(node => {
                            node.readonly = true;
                        });
                    }
                } catch (err) {
                    // Non-fatal: if lock check fails, proceed without forcing readonly
                    console.warn("Warning: Failed to check machine lock state", err);
                }
                    
                setSettings(settings_total);
                let paths_array: Array<any> = path_resolve(settings_total);
                setSettingsArray(paths_array);

                // Run migrations only for machines that support new settings (>= 3.7.0)
                const MIN_MIGRATION_VERSION = '3.7.0';
                if (machine?.version && versionCompare(machine.version, MIN_MIGRATION_VERSION, { zeroExtend: true }) >= 0) {
                    try {
                        for (const migration of migrations) {
                            await migration.migrate(settings_total, machine, appInsights);
                        }
                    } catch (Exception) {
                        const errorObj = Exception instanceof Error ? Exception : new Error(String(Exception));
                        appInsights.trackException({ exception: errorObj, severityLevel: 3, properties: { component: "MachineSettings", action: "migrate", machineVersion: machine?.version ?? "Unknown version", connectionEndpoint: machine?.connection?.ip ?? "Unknown IP" } });
                    }
                }
                setSettingsLoaded(true);
            }
            catch (err) {
                console.log('ERROR', err)
                const typedError = err instanceof Error ? err : undefined;
                handleSettingsError(typedError, "Failed to load Settings from Machine", "load settings");
            }
        };

        loadSettings();

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [machine]);

    async function updateMachineLocation(e: any) {
        const result = await fetch(`${MY_HOST}/api/Matchmaking/EditLocation`, {
            method: 'POST',
            credentials: 'include',
            body: `location=${e}&id=${machine.matchmakingID}`,
            headers: {
                'Content-Type': 'application/x-www-form-urlencoded',
            },
        })
            .then(data => {
                if (data.ok) {
                    return data.json();
                } else {
                    alert("failed to update machine location");
                }
            })
            .then(data => {
                if (data.error == null) {
                    console.log('machine location updated');
                } else {
                    console.log(data.error)
                    alert("failed to update machine location");
                }
            })
        return result;
    }

    const styles: any = {
        modal: {
            position: 'absolute' as 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: {
                md: "55%",
                xs: "90%"
            },
            height: "75%",
            bgcolor: 'background.paper',
            border: '2px solid var(--spectre-border)',
            boxShadow: 24,
            p: 4,
            overflow: 'auto',
            borderRadius: '16px'
        },
        closeButton: {
            fontWeight: 700,
        },
        tabList: {
            allowScrollButtonsMobile: true
        },
        machineSettingsText: {
            fontSize: 28,
            fontWeight: 500,
            color: 'var(--spectre-settings-text-primary)',
        }
    }

    return (
        <Modal
            open={isOpen}
            onClose={toggleMachineSettings}
        >
            <Grid container>
                <Box sx={styles.modal}>
                    <Grid container
                        direction="row"
                        justifyContent="space-between"
                    >
                        <Grid item md={6} xs={11}>
                            <Typography sx={styles.machineSettingsText}>{t("control.settings.settingsTitle")}</Typography>
                        </Grid>
                        <Grid item md={6} xs={1}>
                            <Button className="Blue-Button Modal-Close-Button" sx={styles.closeButton} style={{ float: 'right' }} onClick={toggleMachineSettings}>{t("control.settings.close")}</Button>
                        </Grid>
                    </Grid>
                    <Tabs value={currentTabIndex} variant="scrollable" allowScrollButtonsMobile onChange={handleTabChange}
                        TabIndicatorProps={{
                            style: {
                                backgroundColor: "#D97D54",
                            }
                        }}>
                        {
                            Object.keys(settings).map((e, i) => {
                                return <Tab key={i} sx={{
                                    color: 'var(--spectre-text-secondary)',
                                    "&.Mui-selected": {
                                        color: "#F48430"
                                    }
                                }} label={`${e}`}></Tab>
                            })
                        }
                    </Tabs>
                    {
                        Object.keys(settings).map((e, i) => {

                            let section: any = [];
                            return <CustomTabPanel key={i} value={currentTabIndex} index={i}>

                                {
                                    settingsArray.map((element, index) => {
                                        let path = element['path']

                                        if (path[0] !== e) return '';

                                        let display_name = element['DisplayName']
                                        if (!display_name || display_name === "no name") {
                                            if (Array.isArray(path) && path.length > 0) {
                                                display_name = path[path.length - 1];
                                            }
                                        }

                                        display_name = splitAndFormatString(display_name)

                                        // section header / linebreak logic
                                        let insert_line = false;

                                        if (path.length > 2 && section[1] !== path[1]) {
                                            insert_line = true;
                                            section = path;
                                        }

                                        let lineBreaker = <Typography key={index} sx={{ fontWeight: 500, fontSize: 16, color: 'var(--spectre-settings-text-primary)', pt: 1, pl: 2 }}>{splitAndFormatString(String(path[1]))}</Typography>

                                        let elementType = element['Ui']

                                        switch (true) {
                                            case elementType === 'toggle':
                                                switch (true) {
                                                    case element.readonly:
                                                        return <div key={index}>
                                                            {insert_line ? lineBreaker : ''}
                                                            <ToggleSetting id={display_name} index={index} display_name={display_name} element={element} disabledState={true} setSettingsArray={setSettingsArray}></ToggleSetting>
                                                        </div>
                                                    default:
                                                        return <div key={index}>
                                                            {insert_line ? lineBreaker : ''}
                                                            <ToggleSetting id={display_name} index={index} display_name={display_name} element={element} disabledState={false} setSettingsArray={setSettingsArray} navigateToMatchmaking={navigateToMatchmaking}></ToggleSetting>
                                                        </div>
                                                }
                                            case elementType === 'text':
                                                switch (true) {
                                                    case element.readonly:
                                                        return <div key={index}>
                                                            {insert_line ? lineBreaker : ''}
                                                            <TextSetting id={display_name} inputType={String("text")} element={element} display_name={display_name} updateMachineLocation={updateMachineLocation} disabledState={true}></TextSetting>
                                                        </div>
                                                    default:
                                                        return <div key={index}>
                                                            {insert_line ? lineBreaker : ''}
                                                            <TextSetting id={display_name} inputType={String("text")} element={element} display_name={display_name} updateMachineLocation={updateMachineLocation} disabledState={false} navigateToMatchmaking={navigateToMatchmaking} machine={machine}></TextSetting>
                                                        </div>
                                                }
                                            case elementType === 'password':
                                                switch (true) {
                                                    case element.readonly:
                                                        return <div key={index}>
                                                            {insert_line ? lineBreaker : ''}
                                                            <TextSetting id={display_name} inputType={String("password")} element={element} display_name={display_name} updateMachineLocation={updateMachineLocation} disabledState={true} passwordState={true} navigateToMatchmaking={navigateToMatchmaking}></TextSetting>
                                                        </div>
                                                    default:
                                                        return <div key={index}>
                                                            {insert_line ? lineBreaker : ''}
                                                            <TextSetting id={display_name} inputType={String("password")} element={element} display_name={display_name} updateMachineLocation={updateMachineLocation} disabledState={false} passwordState={true} navigateToMatchmaking={navigateToMatchmaking}></TextSetting>
                                                        </div>
                                                }
                                            case elementType === 'ticker':
                                                switch (true) {
                                                    case element.readonly:
                                                        return <div key={index}>
                                                            {insert_line ? lineBreaker : ''}
                                                            <TextSetting id={display_name} inputType={String("number")} element={element} display_name={display_name} updateMachineLocation={updateMachineLocation} disabledState={true}></TextSetting>
                                                        </div>
                                                    default:
                                                        return <div key={index}>
                                                            {insert_line ? lineBreaker : ''}
                                                            <TextSetting id={display_name} inputType={String("number")} element={element} display_name={display_name} updateMachineLocation={updateMachineLocation} disabledState={false} navigateToMatchmaking={navigateToMatchmaking}></TextSetting>
                                                        </div>
                                                }
                                            case elementType === 'select':
                                                switch (true) {
                                                    case element.readonly:
                                                        return <div key={index}>
                                                            {insert_line ? lineBreaker : ''}
                                                            <DropDownSetting id={display_name} element={element} display_name={display_name} machine={machine} navigateToMatchmaking={navigateToMatchmaking} disabledState={true}></DropDownSetting>
                                                        </div>
                                                    default:
                                                        return <div key={index}>
                                                            {insert_line ? lineBreaker : ''}
                                                            <DropDownSetting id={display_name} element={element} display_name={display_name} machine={machine} navigateToMatchmaking={navigateToMatchmaking} disabledState={false}></DropDownSetting>
                                                        </div>
                                                }
                                            default:
                                                return <div key={index}>
                                                    {insert_line ? lineBreaker : ''}
                                                    {display_name} : {(element['value'])}
                                                </div>
                                        }
                                    })
                                }
                            </CustomTabPanel>
                        })
                    }
                </Box>
            </Grid>
        </Modal>
    )
}